import { CloseIcon } from "@/components/features/SquadBuilder/RightPanel/PanelHeader/PanelHeader";
import { TacticPopularityScorePopup } from "@/components/features/Tactics/TacticPopularityScorePopup";
import DeleteTacticsActionModal from "@/components/molecules/ActionModals/DeleteTacticsActionModal/DeleteTacticsActionModal";
import UploadOrEditTacticsActionModal from "@/components/molecules/ActionModals/UploadOrEditTacticsActionModal/UploadOrEditTacticsActionModal";
import Pagination from "@/components/molecules/Pagination/Pagination";
import CardRow from "@/containers/CardRow";
import TacticChosenSidebar from "@/features/Tactics/Sidebars/TacticChosenSidebar";
import TacticFiltersSidebar from "@/features/Tactics/Sidebars/TacticFiltersSidebar";
import TacticActiveFiltersRow from "@/features/Tactics/TacticActiveFiltersRow";
import TacticCard from "@/features/Tactics/TacticCard";
import TacticHeroSection from "@/features/Tactics/TacticHeroSection";
import TacticToolbar from "@/features/Tactics/TacticToolbar";
import TacticsSidePanel, {
  TacticsSidePanelOption,
} from "@/features/Tactics/TacticsSidePanel";
import { useSticky } from "@/hooks/useSticky";
import { cn } from "@/lib/utils";
import { formationsAsListSelector } from "@/redux/loadedContent/loadedContentSlice";
import { addAlert, showModal } from "@/redux/message/messageSlice";
import { AppDispatch } from "@/redux/store";
import {
  Tactic,
  TacticSortOption,
  TacticSortOptionValues,
  editTactic,
  fetchAuthors,
  fetchTactics,
  setSort,
  tacticsSelector,
  toggleFavoriteFilter,
} from "@/redux/tactics/tacticsSlice";
import { getUserSelector } from "@/redux/user/userSlice";
import { BREAKPOINTS } from "@/settings";
import { Fragment, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useMediaQuery } from "usehooks-ts";

import {
  clearSquad,
  setChosenTactic as setChosenSquadBuilderTactic,
  switchLeftPanelMode,
} from "../../redux/squadBuilder/defaultSquadBuilder/defaultSquadBuilderSlice";

interface TacticEditModalProps {
  tacticName: string;
  tacticsDescription: string;
  tacticsCode: string;
  tacticId: string;
}

interface TacticPopularityScoreBannerProps {
  onClose: () => void;
  onReadMore: () => void;
}
const TacticPopularityScoreBanner = ({
  onClose,
  onReadMore,
}: TacticPopularityScoreBannerProps) => (
  <article className="py-2 w-full touch-device:rounded-lg flex items-center justify-between bg-panel-middleground px-2">
    <p className="text-sm">
      The Popularity Score shows how much each tactic is used by the community,
      based on actual in-game data.{" "}
      <span
        className="text-primary-action underline cursor-pointer"
        onClick={(e) => {
          e.stopPropagation();
          onReadMore();
        }}
      >
        Read more.
      </span>
    </p>
    <button className="p-2" onClick={onClose}>
      <CloseIcon />
    </button>
  </article>
);

export const TacticsPage = () => {
  const dispatch = useDispatch<AppDispatch>();
  const [scrollIntoBottom, refBottomBar] = useSticky({
    triggerOn: "inView",
  }) as [boolean, React.MutableRefObject<HTMLDivElement>];
  const {
    tactics,
    responses,
    sort,
    totalPages,
    selectedFilters,
    isFavoriteFilterOn,
  } = useSelector(tacticsSelector);
  const { user } = useSelector(getUserSelector);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const formationsAsList = useSelector(formationsAsListSelector);
  const [panelState, setPanelState] = useState<TacticsSidePanelOption>();
  const [activeTactic, setActiveTactic] = useState<Tactic>();
  const [showUploadTacticsModal, setShowUploadTacticsModal] = useState(false);
  const [showEditTacticsModal, setShowEditTacticsModal] =
    useState<TacticEditModalProps>();
  const [showDeleteTacticsModal, setShowDeleteTacticsModal] = useState<{
    tacticId: string;
  }>();
  const [page, setPage] = useState(1);
  const isLargeScreen = useMediaQuery(BREAKPOINTS.SMALL_DESKTOP.query);
  const isMobile = useMediaQuery(BREAKPOINTS.MOBILE.query);
  const tacticLimit = useMemo(() => (isLargeScreen ? 10 : 8), [isLargeScreen]);
  const [showPopularityScoreBanner, setShowPopularityScoreBanner] = useState(
    localStorage.getItem("hasClosedPopularityScoreBanner") !== "true",
  );
  const [showPopularityScorePopup, setShowPopularityScorePopup] =
    useState(false);

  const handleClosePopularityScoreBanner = () => {
    localStorage.setItem("hasClosedPopularityScoreBanner", "true");
    setShowPopularityScoreBanner(false);
  };

  useEffect(() => {
    dispatch(fetchAuthors());
  }, [dispatch]);

  useEffect(() => {
    const fetchData = async () => {
      setPage(1);
      await dispatch(fetchTactics({ page: 1, formationsAsList, tacticLimit }));
      window.scrollBy(0, 1);
      window.scrollBy(0, -1);
    };
    fetchData();
  }, [dispatch, sort, selectedFilters, isFavoriteFilterOn]);

  useEffect(() => {
    const sortParam = searchParams.get("sort") as TacticSortOptionValues;
    if (sortParam) {
      const sortOption: TacticSortOption = {
        value: sortParam,
        label:
          sortParam === "popularity-score"
            ? "Popularity Score"
            : sortParam === "date-added"
              ? "Date Added"
              : "Recommended",
      };
      dispatch(setSort(sortOption));
    }
  }, [searchParams, dispatch]);

  const handleSortByChanged = (option: TacticSortOption) => {
    setSearchParams({
      sort: option.value,
    });
    dispatch(setSort(option));
  };

  const handleChangePage = (page: number) => {
    setPage(page);
    dispatch(fetchTactics({ page, formationsAsList, tacticLimit }));
    if (window.scrollY > 262 && !isMobile) {
      window.scrollTo(0, 262);
    } else if (isMobile && window.scrollY > 192) {
      window.scrollTo(0, 192);
    }
  };

  const handleBuildSquadWithTactic = (tactic: Tactic) => {
    dispatch(
      setChosenSquadBuilderTactic({
        tactic: tactic,
        fromTacticsPage: true,
        initialLoad: false,
      }),
    );
    dispatch(clearSquad());
    dispatch(switchLeftPanelMode("tactics"));
    navigate("/squad-builder");
    window.scrollTo(0, 0);
    dispatch(
      addAlert({
        title: "Success",
        description: `Tactic ${tactic.name} applied to squad builder`,
        type: "success",
      }),
    );
  };

  const handleCopy = (eaCode: string) => {
    navigator.clipboard.writeText(eaCode).then(
      () => {
        dispatch(
          addAlert({
            title: "Success",
            description: "Copied to clipboard",
            type: "success",
          }),
        );
      },
      (err) => {
        console.error("Failed to copy: ", err);
      },
    );
  };

  const handleClickFavorite = async (tactic: Tactic) => {
    if (user?.uuid) {
      if (activeTactic) {
        setActiveTactic({
          ...activeTactic,
          isFavorite: !activeTactic.isFavorite,
        });
      }
      await dispatch(
        editTactic({ id: tactic.id, isFavorite: !tactic.isFavorite }),
      );
      dispatch(
        addAlert({
          title: "Favorite",
          description: `Tactic ${tactic.name} ${
            !tactic.isFavorite ? "added to" : "removed from"
          } favorites`,
          type: "success",
        }),
      );
    } else {
      dispatch(
        showModal({
          modalType: "login-required",
          freeFeatureLoggedInRequiredHook: "favorite-tactic",
        }),
      );
    }
  };

  const handleCloseSidebar = () => {
    setPanelState(undefined);
    setActiveTactic(undefined);
  };

  return (
    <div className="flex flex-row w-full mx-auto bg-panel-base flex-1 h-full">
      <div className="flex flex-col items-center w-full shrink">
        <TacticHeroSection />
        <TacticToolbar
          panelState={panelState}
          isFavoriteFilterOn={isFavoriteFilterOn}
          loading={responses.getTactics.status === "pending"}
          canUploadTactic={user?.canUploadTactic}
          handleFiltersClicked={() =>
            setPanelState(panelState === "filters" ? undefined : "filters")
          }
          handleFavoriteClicked={() => {
            if (user?.uuid) {
              dispatch(toggleFavoriteFilter(!isFavoriteFilterOn));
            } else {
              dispatch(
                showModal({
                  modalType: "login-required",
                  freeFeatureLoggedInRequiredHook: "favorite-tactic",
                }),
              );
            }
          }}
          currentSortBy={sort}
          handleSortByChanged={handleSortByChanged}
          handleUploadTacticsClicked={() => {
            setShowUploadTacticsModal(true);
          }}
        />
        {(showUploadTacticsModal || showEditTacticsModal) && (
          <UploadOrEditTacticsActionModal
            onDismiss={() => {
              setShowUploadTacticsModal(false);
              setShowEditTacticsModal(undefined);
            }}
            type={showUploadTacticsModal ? "upload" : "edit"}
            tacticName={showEditTacticsModal?.tacticName || ""}
            tacticsCode={showEditTacticsModal?.tacticsCode || ""}
            tacticsDescription={showEditTacticsModal?.tacticsDescription || ""}
            tacticId={showEditTacticsModal?.tacticId || ""}
            responses={responses}
          />
        )}
        {showDeleteTacticsModal && (
          <DeleteTacticsActionModal
            onDismiss={() => setShowDeleteTacticsModal(undefined)}
            tacticId={showDeleteTacticsModal.tacticId}
            responses={responses}
          />
        )}
        {showPopularityScorePopup && (
          <TacticPopularityScorePopup
            onClose={() => setShowPopularityScorePopup(false)}
          />
        )}
        <div className="flex flex-row w-full max-w-content">
          <div className="flex flex-col text-white gap-y-2 w-full mt-2 desktop:px-2 large:px-16">
            <div className="flex flex-row gap-x-2">
              <div
                className={cn(
                  "flex flex-col gap-y-2 w-full",
                  panelState && "hidden mobile:flex",
                )}
              >
                <TacticActiveFiltersRow />
                {showPopularityScoreBanner && (
                  <TacticPopularityScoreBanner
                    onClose={handleClosePopularityScoreBanner}
                    onReadMore={() => setShowPopularityScorePopup(true)}
                  />
                )}
                {tactics.length > 0
                  ? tactics.map((tactic, index) => (
                      <Fragment key={tactic.id}>
                        <TacticCard
                          tactic={tactic}
                          key={tactic.id}
                          active={activeTactic?.id === tactic.id}
                          handleCopy={handleCopy}
                          handleClickFavorite={() =>
                            handleClickFavorite(tactic)
                          }
                          handleClickTacticCard={(tactic) => {
                            setActiveTactic(
                              activeTactic?.id === tactic.id
                                ? undefined
                                : tactic,
                            );
                            setPanelState(
                              panelState === "chosenTactic" &&
                                activeTactic?.id === tactic.id
                                ? undefined
                                : "chosenTactic",
                            );
                          }}
                          handleEditTactic={() =>
                            setShowEditTacticsModal({
                              tacticName: tactics[index].name,
                              tacticsDescription: tactics[index].description,
                              tacticsCode: tactics[index].eaCode,
                              tacticId: tactics[index].id,
                            })
                          }
                          handleDeleteTactic={() =>
                            setShowDeleteTacticsModal({
                              tacticId: tactics[index].id,
                            })
                          }
                        />
                        {index === tactics.length - 1 &&
                          tactics.length === tacticLimit && (
                            <div ref={refBottomBar} />
                          )}
                      </Fragment>
                    ))
                  : (responses.getTactics.status === "rejected" ||
                      responses.getTactics.status === "fulfilled") && (
                      <CardRow>
                        <div className="m-1 w-full bg-gray-900 flex items-center justify-center">
                          No tactics found with your filters
                        </div>
                      </CardRow>
                    )}
              </div>
              {panelState && (
                <TacticsSidePanel
                  sidebarOption={panelState}
                  handleCloseSidebar={handleCloseSidebar}
                  handleBuildSquadWithTactic={() =>
                    activeTactic && handleBuildSquadWithTactic(activeTactic)
                  }
                  scrollIntoBottom={scrollIntoBottom}
                >
                  {panelState === "chosenTactic" && activeTactic ? (
                    <TacticChosenSidebar
                      tactic={activeTactic}
                      handleCopy={handleCopy}
                      isFavorite={activeTactic.isFavorite}
                      handleClickFavorite={() =>
                        handleClickFavorite(activeTactic)
                      }
                    />
                  ) : panelState === "filters" ? (
                    <TacticFiltersSidebar />
                  ) : null}
                </TacticsSidePanel>
              )}
            </div>
            {tactics.length > 0 && (
              <div className="flex justify-end w-full pt-2 pb-6">
                <Pagination
                  currentPage={page}
                  totalAmountOfPages={totalPages}
                  changePage={handleChangePage}
                />
                {tactics.length < tacticLimit && <div ref={refBottomBar} />}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
